import * as Sentry from "@sentry/vue"
import { useNetwork, useSessionStorage } from "@vueuse/core"
import NProgress from "nprogress"
import { createRouter, createWebHistory } from "vue-router"
import { handleHotUpdate, routes } from "vue-router/auto-routes"
import { externalRedirects, redirectRoutes } from "@/redirects"
import { addSlugAliases, resolveSlugPersistedPath } from "@/slugRoutes"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { registry } from "./composables/useModal"
import { useAuthStore } from "./stores/authStore"

import "nprogress/nprogress.css"

addSlugAliases(routes)

for (const route of redirectRoutes) {
  routes.push(route)
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition)
      return savedPosition
    if (to.fullPath === from.fullPath)
      return false
    if (to.hash)
      return { el: to.hash }
    return { left: 0, top: 0 }
  },
})

if (import.meta.hot) {
  handleHotUpdate(router)
}

router.beforeEach((to, from) => {
  // Persists the current vanity slug across imperative navigation
  // (`router.push`/`router.replace`), mirroring what `AppLink` already does
  // for `<RouterLink>` clicks. Registered ahead of the guard below so a
  // redirect here restarts the guard chain before any of that guard's
  // side effects (NProgress, store updates) run for the un-prefixed target.
  const redirectLocation = resolveSlugPersistedPath(from, to)
  if (redirectLocation) {
    return { ...redirectLocation, replace: true }
  }
})

router.beforeEach(async (to, from) => {
  // Strip the vanity slug prefix from the path before checking for external
  // redirects — when matched via a `/:slug?/...` alias, `to.path` starts with
  // `/<slug>/...` but `externalRedirects` are defined without a slug prefix.
  const rawSlug = (to.params as Record<string, string | string[] | undefined>).slug
  const slugPrefix = typeof rawSlug === "string" && rawSlug ? `/${rawSlug}` : ""
  const pathWithoutSlug = slugPrefix ? to.path.slice(slugPrefix.length) : to.path
  if (externalRedirects.some(path => pathWithoutSlug.toLowerCase().startsWith(path))) {
    window.location.replace(`${import.meta.env.VITE_BASE_API_URL}${to.fullPath}`)
    return
  }

  if (to.path !== from.path) {
    // Only tear down modals / restart progress on a genuine page change. When the
    // slug is re-prefixed onto an already-mounted route, `to.name` stays identical
    // (see `addSlugAliases`), so the component instance is reused and its setup()
    // — which registers modals — does not re-run. Clearing the registry here would
    // orphan those modals.
    if (to.name !== from.name) {
      registry.clear()
      NProgress.start()
    }

    const getIdParam = (param?: string | string[]) => {
      const value = Array.isArray(param) ? param[0] : param
      if (!value)
        return undefined
      const num = Number(value)
      return Number.isNaN(num) ? undefined : num
    }
    const param = to.params as Record<string, string | string[] | undefined>

    // `to.name` stays the same whether the route was matched via its
    // canonical path or its `/:slug` prefixed alias (see `addSlugAliases`),
    // unlike `to.path` which would gain the slug segment.
    const routeName = typeof to.name === "string" ? to.name : ""

    let eventVaultId: number | undefined
    if (routeName.startsWith("/event/") || routeName.startsWith("/guest/")) {
      eventVaultId = getIdParam(param.id)
    }

    const albumId = getIdParam(param.albumId)
    const slugValue = param.slug
    const slug = Array.isArray(slugValue) ? slugValue[0] : slugValue

    const { updateStateFromParams } = useEventVaultStore()
    updateStateFromParams({ eventVaultId, albumId, slug })
  }

  const authStore = useAuthStore()
  await authStore.initialize()

  if (to.meta?.requiresAuth && !authStore.isAuthenticated) {
    return { name: "/login", query: { redirect: to.fullPath } }
  }
})

const lastRouterRetry = useSessionStorage<number | null>("router-import-retry", null)
const COOLDOWN = 10_000

const { isOnline } = useNetwork()

router.onError((error, to, from) => {
  NProgress.done()

  const message = error?.message ?? ""

  const failedToLoadModule = (
    message.includes("Failed to fetch dynamically imported module")
    || message.includes("Importing a module script failed")
    || message.includes("ChunkLoadError")
    || message.includes("Loading chunk")
    || message.includes("CSS_CHUNK_LOAD_FAILED")
    || message.includes("Unable to preload CSS")
    || message.includes("'text/html' is not a valid JavaScript MIME type.")
    // Facebook in-app browser (iOS) fetches stale chunks that the SPA server
    // returns as index.html (200 OK). The browser parses HTML as JS and throws
    // a SyntaxError instead of the standard "Failed to fetch" message.
    || message.includes("Unexpected token '<'")
    || message.toLowerCase().includes("<!doctype")
  )

  if (!failedToLoadModule) {
    Sentry.withScope((scope) => {
      scope.setTag("area", "router")
      scope.setContext("route", { to: to?.path ?? "unknown", from: from?.path ?? "unknown" })

      Sentry.captureException(error)
    })
  }

  if (!failedToLoadModule || !isOnline)
    return

  if (lastRouterRetry.value && Date.now() - lastRouterRetry.value < COOLDOWN)
    return

  lastRouterRetry.value = Date.now()

  window.location.reload()
})

router.afterEach(() => NProgress.done())

router.isReady().then(() => lastRouterRetry.value = null)

export default router
